import type { TextInputVariant } from "@stenajs-webui/forms";
import type { DropdownOption, SelectVariant } from "@stenajs-webui/select";
import { first, keys } from "lodash";
import { type ReactNode, useCallback, useMemo } from "react";
import type {
  FilterOptionOption,
  GroupBase,
  Options,
  SelectComponentsConfig,
} from "react-select";

import type { GroupedOptionsType } from "../../common-ui/form-fields/Dropdown";
import { GoodsTypeDropdownSingleValue } from "../../common-ui/goods/GoodsTypeDropdownSingleValue";
import type { TrackerAction } from "../../use-cases/simple-create/hooks/UseTrackingTextInput";
import {
  MAX_LENGTH_GOODS_DESCRIPTION,
  MAX_LENGTH_GOODS_WEIGHT,
} from "../fieldLengths";
import type { GoodsType } from "../graphql/fragments/gql/GoodsType";
import { animalDropdownNoOption } from "../graphql/useAnimalTypesOptions";
import type { UseRegistrationNumberAlertApplied } from "../registrationNumberAlert/utils/arrivalNotepadUtil";
import { type TrackerCategory, trackEvent } from "../tracking/trackerService";
import { addRecentToOptions } from "../utils/DropdownUtils";
import type { MinimalBooking } from "../utils/minimalBooking";
import type { Animal } from "../utils/optionTransformers";
import { transformGoodsTypeOption } from "../utils/optionTransformers";
import {
  filterGoodsTypes,
  type GoodsItem,
  type GoodsItems,
  getGoodsItemPlaceholder,
  getNewGoodsItem,
  getShowEnsGoodsBanner,
  getTotalWeightFromGoodsItems,
  noneGoodsTypeOption,
  removeGoodsItem,
  setGoodsItem,
  updateGoodsItemDescription,
  updateGoodsItemType,
  updateGoodsItemWeight,
} from "./goodsUtils";

interface UseGoodsParams {
  arrivalNotepadInfo: UseRegistrationNumberAlertApplied;
  goodsItems: GoodsItems;
  goodsTypes: GoodsType[];
  livestockType: Animal | null | undefined;
  onChange: (goodsItems: GoodsItems) => void;
  recentGoodsTypeCodes: string[];
  trackerCategory: TrackerCategory;
}

export const useGoods = ({
  goodsTypes,
  goodsItems,
  onChange,
  trackerCategory,
  recentGoodsTypeCodes,
  arrivalNotepadInfo,
  livestockType,
}: UseGoodsParams) => {
  const hasAnimals =
    livestockType != null &&
    livestockType.value !== animalDropdownNoOption.value;
  const dropdownOptions:
    | GroupedOptionsType<DropdownOption<GoodsType>>
    | Options<DropdownOption<GoodsType>> = useMemo(() => {
    const opts = goodsTypes.map((g) => transformGoodsTypeOption(g));

    if (recentGoodsTypeCodes.length > 0) {
      return addRecentToOptions(opts, recentGoodsTypeCodes);
    }

    return opts;
  }, [goodsTypes, recentGoodsTypeCodes]);

  const onAddGoodsItem = () => {
    trackEvent({ category: trackerCategory, action: "Add goods" });
    onChange(setGoodsItem(goodsItems, getNewGoodsItem()));
  };

  const totalGoodsWeight = useMemo(
    () => getTotalWeightFromGoodsItems(goodsItems),
    [goodsItems],
  );

  const onChangeDescription = useCallback(
    (goodsItem: GoodsItem, value: string) =>
      onChange(
        setGoodsItem(goodsItems, updateGoodsItemDescription(goodsItem, value)),
      ),
    [goodsItems, onChange],
  );

  const onChangeWeight = useCallback(
    (goodsItem: GoodsItem, value: number | undefined) =>
      onChange(
        setGoodsItem(goodsItems, updateGoodsItemWeight(goodsItem, value)),
      ),
    [goodsItems, onChange],
  );

  const onChangeType = useCallback(
    (goodsItem: GoodsItem, value: GoodsType) => {
      if (value === noneGoodsTypeOption.data) {
        trackEvent({ category: trackerCategory, action: "Remove goods" });
        onChange(removeGoodsItem(goodsItems, goodsItem.id));
      } else {
        onChange(
          setGoodsItem(goodsItems, updateGoodsItemType(goodsItem, value)),
        );
      }
    },
    [goodsItems, onChange, trackerCategory],
  );

  const getWeightInputProps = useCallback(
    (
      goodsItem: GoodsItem,
      isValid: boolean,
    ): {
      onlyIntegers: true;
      onValueChange: (value: number | undefined) => void;
      value: number | undefined;
      variant?: TextInputVariant;
      maxLength: number;
      trackerAction: TrackerAction;
      trackerCategory: TrackerCategory;
      disableContentPaddingRight: boolean;
      contentRight: ReactNode | null;
    } => {
      // Arrival notepad is only applicable to the first goods item, as it is the one that is created when applying
      // arrival notepad.
      const arrivalNotepadIcon =
        goodsItem.id === first(keys(goodsItems))
          ? arrivalNotepadInfo.icon
          : null;

      return {
        onlyIntegers: true,
        onValueChange: (value: number | undefined) =>
          onChangeWeight(goodsItem, value),
        value: goodsItem.weight,
        variant: !isValid ? "error" : undefined,
        maxLength: MAX_LENGTH_GOODS_WEIGHT,
        disableContentPaddingRight: true,
        contentRight: arrivalNotepadIcon,
        trackerAction: { value: "Set goods weight" },
        trackerCategory,
      };
    },
    [arrivalNotepadInfo.icon, goodsItems, onChangeWeight, trackerCategory],
  );

  const getDescriptionInputProps = useCallback(
    (
      goodsItem: GoodsItem,
      isValid: boolean,
    ): {
      onValueChange: (value: string) => void;
      value: string;
      maxLength: number;
      variant: TextInputVariant | undefined;
      placeholder?: string;
      trackerAction: TrackerAction;
      trackerCategory: TrackerCategory;
    } => ({
      onValueChange: (value: string) => onChangeDescription(goodsItem, value),
      value: goodsItem.description,
      maxLength: MAX_LENGTH_GOODS_DESCRIPTION,
      placeholder: getGoodsItemPlaceholder(goodsItem),
      variant: !isValid ? "error" : undefined,
      trackerAction: { value: "Set goods description" },
      trackerCategory,
    }),
    [onChangeDescription, trackerCategory],
  );

  const getTypeDropdownProps = useCallback(
    (
      goodsItem: GoodsItem,
      isValid: boolean,
    ): {
      onValueChange: (value: GoodsType) => void;
      value: GoodsType | undefined;
      options:
        | GroupedOptionsType<DropdownOption<GoodsType>>
        | Options<DropdownOption<GoodsType>>;
      transformOption: (goodsType: GoodsType) => DropdownOption<GoodsType>;
      variant?: SelectVariant;
      trackerAction: TrackerAction;
      trackerCategory: TrackerCategory;
      filterOption: (
        option: FilterOptionOption<DropdownOption<GoodsType>>,
      ) => boolean;
      components: SelectComponentsConfig<
        DropdownOption<NonNullable<GoodsType>>,
        false,
        GroupBase<DropdownOption<NonNullable<GoodsType>>>
      >;
    } => ({
      onValueChange: (value: GoodsType) => onChangeType(goodsItem, value),
      value: goodsItem.goodsType ?? undefined,
      options: dropdownOptions,
      transformOption: transformGoodsTypeOption,
      variant: !isValid ? "error" : undefined,
      trackerAction: { value: "Set goods type" },
      trackerCategory,
      filterOption: (option) => filterGoodsTypes(option, hasAnimals),
      components: { SingleValue: GoodsTypeDropdownSingleValue },
    }),
    [onChangeType, dropdownOptions, trackerCategory, hasAnimals],
  );

  return {
    onAddGoodsItem,
    totalGoodsWeight,
    getWeightInputProps,
    getDescriptionInputProps,
    getTypeDropdownProps,
  };
};

export const useShowEnsGoodsBanner = (
  minimalBooking: MinimalBooking | null | undefined,
  options?: { ensConfirmed: boolean },
) => {
  return useMemo(() => {
    if (!minimalBooking) {
      return false;
    }

    return getShowEnsGoodsBanner(minimalBooking, options);
  }, [minimalBooking, options]);
};
